import { z } from 'zod';
import { LoginSchema, PasswordSchema } from './auth.js';
import { SpaceRoleSchema } from './spaces.js';
/**
 * Контракты эндпоинтов `/api/admin/users` (см. `docs/modules/auth.md §4`,
 * `docs/REQUIREMENTS.md FR-0`). Доступны только платформ-админам
 * (`requirePlatformAdmin`). Используются формой «Создать участника»
 * в M2 перед добавлением в пространство.
 */
// ─── PlatformRole ────────────────────────────────────────────────────────
//
// Платформенный уровень прав, см. SRS §2.2. На уровне БД хранится как
// boolean `users.is_platform_admin`, но во внешних DTO (audit, ответы
// /me, фильтры) удобно ходить с явным enum'ом из двух значений.
export const PLATFORM_ROLES = ['platform_admin', 'user'];
export const PlatformRoleSchema = z.enum(PLATFORM_ROLES);
// ─── AdminUser DTO ───────────────────────────────────────────────────────
export const AdminUserSchema = z.object({
    id: z.string().uuid(),
    login: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
    isActive: z.boolean(),
    isPlatformAdmin: z.boolean(),
    createdAt: z.string(),
    // Связанный телеграм-аккаунт: `null`, если человек ещё не привязал.
    // Нужно, чтобы видеть, чьи записи из чатов останутся без автора.
    telegramUsername: z.string().nullable().optional(),
    telegramLinked: z.boolean().optional(),
    /** Нажал «Start» у бота — бот может писать ему первым (личные уведомления). */
    telegramBotChatOpened: z.boolean().optional(),
    /**
     * Личные уведомления в Telegram: 'on' — включены (по умолчанию),
     * 'partial' — выключены в части пространств, 'off' — выключены совсем.
     */
    telegramNotify: z.enum(['on', 'partial', 'off']).optional(),
    avatarUrl: z.string().nullable().optional(),
    // ─── Когда был и что подключил (список и карточка пользователя) ─────
    /** Последний вход паролем или через SSO; null — ни разу не входил. */
    lastLoginAt: z.string().nullable().optional(),
    /** Последний запрос из браузера; null — ни разу не заходил. */
    lastSeenAt: z.string().nullable().optional(),
    /** Как входит: через корпоративный SSO или локальным паролем. */
    authMethod: z.enum(['sso', 'password']).optional(),
    /** Действующие MCP-токены (не отозваны и не истекли). */
    mcpTokens: z.number().int().optional(),
    /** Когда MCP-токены пользователя последний раз использовались. */
    mcpLastUsedAt: z.string().nullable().optional(),
    jiraConnected: z.boolean().optional(),
    llmKeyConnected: z.boolean().optional(),
    spacesCount: z.number().int().optional(),
});
// ─── GET /api/admin/users/:id/overview ──────────────────────────────────
//
// Всё, что админ видит в карточке пользователя: входы, активность по дням,
// подключения (MCP, Telegram, Jira, ключ LLM) и где человек работает.
export const AdminUserLoginEventSchema = z.object({
    event: z.string(),
    createdAt: z.string(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
});
export const AdminUserTokenSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    prefix: z.string(),
    createdAt: z.string(),
    lastUsedAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
});
export const AdminUserOverviewSchema = z.object({
    user: AdminUserSchema,
    /** Период, за который считаются `counts` и `logins`, в днях. */
    periodDays: z.number().int(),
    logins: z.object({
        success: z.number().int(),
        failed: z.number().int(),
        /** Последние события входа/выхода, новые сверху. */
        recent: z.array(AdminUserLoginEventSchema),
    }),
    counts: z.object({
        itemsCreated: z.number().int(),
        changes: z.number().int(),
        comments: z.number().int(),
        aiRequests: z.number().int(),
    }),
    /** Действия по дням (изменения + комментарии + запросы к ИИ + входы), YYYY-MM-DD. */
    daily: z.array(z.object({ date: z.string(), count: z.number().int() })),
    tokens: z.array(AdminUserTokenSchema),
    telegram: z
        .object({
        username: z.string().nullable(),
        name: z.string().nullable(),
        linkedAt: z.string().nullable(),
        lastSeenAt: z.string().nullable(),
        /** Когда нажал «Start» у бота; null — бот не может писать первым. */
        botChatOpenedAt: z.string().nullable(),
        /** Личные уведомления: выключены совсем и названия выключенных пространств. */
        notifyEnabled: z.boolean(),
        mutedSpaces: z.array(z.string()),
    })
        .nullable(),
    /** Пространства, где человек участник, с ролью и числом его изменений за период. */
    spaces: z.array(z.object({
        spaceId: z.string().uuid(),
        name: z.string(),
        slug: z.string(),
        icon: z.string().nullable(),
        isArchived: z.boolean(),
        role: SpaceRoleSchema.nullable(),
        changes: z.number().int(),
    })),
});
// ─── POST /api/admin/users ───────────────────────────────────────────────
export const CreateAdminUserRequestSchema = z.object({
    login: LoginSchema,
    displayName: z.string().trim().min(1).max(120),
    email: z.string().email().max(200).optional(),
    password: PasswordSchema,
    isPlatformAdmin: z.boolean().default(false),
});
// ─── PATCH /api/admin/users/:id ──────────────────────────────────────────
export const UpdateAdminUserRequestSchema = z.object({
    displayName: z.string().trim().min(1).max(120).optional(),
    email: z.string().email().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
    isPlatformAdmin: z.boolean().optional(),
});
// ─── POST /api/admin/users/:id/password ──────────────────────────────────
export const ResetAdminUserPasswordRequestSchema = z.object({
    password: PasswordSchema,
});
// ─── GET /api/admin/users ────────────────────────────────────────────────
export const AdminUserListResponseSchema = z.object({
    users: z.array(AdminUserSchema),
});
// ─── Доступ пользователя к пространствам (admin) ─────────────────────────
//
// Платформ-админ управляет членством пользователя сразу во всех
// пространствах из карточки пользователя, не заходя в каждое
// пространство по отдельности (`POST /api/spaces/:id/members`).
//
//   GET /api/admin/users/:id/spaces — все пространства + текущая роль
//   PUT /api/admin/users/:id/spaces — полная синхронизация членства
/** Строка матрицы доступа: пространство + роль пользователя (null = не участник). */
export const AdminUserSpaceSchema = z.object({
    spaceId: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    icon: z.string().nullable(),
    isArchived: z.boolean(),
    /** Текущая роль пользователя в пространстве; null — доступа нет. */
    role: SpaceRoleSchema.nullable(),
});
export const AdminUserSpacesResponseSchema = z.object({
    spaces: z.array(AdminUserSpaceSchema),
});
/**
 * PUT /api/admin/users/:id/spaces — желаемый набор членства. Сервер
 * сравнивает его с текущим и применяет дельту: добавляет новых,
 * меняет роль изменившимся, удаляет отсутствующих в списке. Пространства
 * без записи в `memberships` означают «доступа нет».
 */
export const SetAdminUserSpacesRequestSchema = z.object({
    memberships: z.array(z.object({
        spaceId: z.string().uuid(),
        role: SpaceRoleSchema,
    })),
});
// ─── SSO / Keycloak роли (Phase 5) ──────────────────────────────────────
//
// Вход в Lens через корпоративный Keycloak (OIDC). Доступ и платформенные
// права выдаются НЕ группами Keycloak напрямую, а client-ролями клиента
// `lens` (`resource_access.lens.roles`). Группы Keycloak остаются
// оргединицей — им назначают роли, приложение проверяет РОЛЬ.
// См. `docs/modules/auth.md §8`.
//
// Каталог — полный список ролей хаба (справочник для UI и валидации выбора).
// Из него админ выбирает, какая роль = доступ в Lens, какая = платформ-админ.
export const DEFAULT_SSO_ROLE_CATALOG = [
    'lens-access',
    'lens-admin',
    'hermes-access',
    'hermes-admin',
    'dochub-access',
    'dochub-admin',
    'rag-access',
    'rag-admin',
    'ai-access',
    'ai-admin',
];
/** Роль, наличие которой в токене = право войти в Lens. */
export const DEFAULT_SSO_ACCESS_ROLE = 'lens-access';
/** Роль, наличие которой = платформенный админ Lens (is_platform_admin). */
export const DEFAULT_SSO_ADMIN_ROLE = 'lens-admin';
/** Имя роли: непустая строка ≤120 символов. */
const RoleNameSchema = z.string().trim().min(1).max(120);
// ─── Platform Settings ──────────────────────────────────────────────────
export const JiraModeSchema = z.enum(['test', 'prod']);
/** GET /api/admin/settings — текущие глобальные настройки. */
export const PlatformSettingsResponseSchema = z.object({
    jiraMode: JiraModeSchema,
    jiraBaseUrl: z.string().nullable(),
    jiraBaseUrlTest: z.string().nullable(),
    jiraBaseUrlEffective: z.string().nullable(),
    llmBaseUrl: z.string().nullable(),
    llmBaseUrlEffective: z.string().nullable(),
    llmModel: z.string().nullable(),
    llmModelEffective: z.string().nullable(),
    // ─── SSO / Keycloak ───────────────────────────────────────────────
    /** Включён ли вход через Keycloak (кнопка + приём callback). */
    ssoEnabled: z.boolean(),
    /**
     * Заданы ли серверные реквизиты клиента (KC_ISSUER/CLIENT_ID/SECRET/
     * REDIRECT_URI в env). Read-only сигнал: без них `ssoEnabled` не даст
     * рабочего входа — UI показывает предупреждение.
     */
    ssoConfigured: z.boolean(),
    /** Эффективный каталог ролей (из БД либо дефолтный). */
    ssoRoleCatalog: z.array(z.string()),
    /** Эффективная роль доступа. */
    ssoAccessRole: z.string(),
    /** Эффективная роль платформ-админа. */
    ssoAdminRole: z.string(),
    // ─── Реквизиты клиента Keycloak (эффективные: БД → env fallback) ──
    ssoIssuer: z.string().nullable(),
    ssoClientId: z.string().nullable(),
    ssoRedirectUri: z.string().nullable(),
    ssoPostLogoutUri: z.string().nullable(),
    /** Задан ли секрет клиента (сам секрет наружу не отдаём). */
    ssoHasClientSecret: z.boolean(),
    updatedAt: z.string(),
});
/** PATCH /api/admin/settings — обновление настроек. */
export const UpdatePlatformSettingsRequestSchema = z.object({
    jiraMode: JiraModeSchema.optional(),
    jiraBaseUrl: z.string().url().nullable().optional(),
    jiraBaseUrlTest: z.string().url().nullable().optional(),
    llmBaseUrl: z.string().url().nullable().optional(),
    llmModel: z.string().nullable().optional(),
    ssoEnabled: z.boolean().optional(),
    ssoRoleCatalog: z.array(RoleNameSchema).max(100).optional(),
    ssoAccessRole: RoleNameSchema.optional(),
    ssoAdminRole: RoleNameSchema.optional(),
    // Реквизиты клиента Keycloak. `null`/'' очищает поле. `ssoClientSecret`
    // write-only: непустая строка → зашифровать и сохранить; null → удалить;
    // undefined (не прислано) → не трогать.
    ssoIssuer: z.string().url().nullable().optional(),
    ssoClientId: z.string().trim().max(255).nullable().optional(),
    ssoRedirectUri: z.string().url().nullable().optional(),
    ssoPostLogoutUri: z.string().url().nullable().optional(),
    ssoClientSecret: z.string().max(2048).nullable().optional(),
});
// ─── Загрузка пользователей из Keycloak (Phase 5) ───────────────────────
//
// Админ подтягивает сотрудников из Keycloak и заводит их в Lens ДО первого
// входа — чтобы добавлять в пространства заранее. Читаем Keycloak Admin REST
// API от имени service account того же клиента, что и вход по OIDC.
// См. `docs/modules/auth.md §8.8`.
/** Откуда брать список: члены ролей доступа / поиск по строке / члены группы. */
export const KeycloakUserSourceSchema = z.enum(['roles', 'search', 'group']);
/**
 * Что произойдёт с кандидатом при загрузке:
 *   new       — заведём нового пользователя;
 *   link      — есть запись Lens с тем же логином/почтой и без связки → свяжем;
 *   exists    — уже связан с этим аккаунтом Keycloak, делать нечего;
 *   conflict  — логин занят записью, связанной с другим аккаунтом Keycloak;
 *   disabled  — пользователь выключен в Keycloak, не заводим.
 */
export const KeycloakCandidateStatusSchema = z.enum([
    'new',
    'link',
    'exists',
    'conflict',
    'disabled',
]);
export const KeycloakCandidateSchema = z.object({
    /** id пользователя в Keycloak — он же будущий `sub` в токене. */
    keycloakId: z.string(),
    login: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
    enabled: z.boolean(),
    /** Роли клиента Lens, найденные у пользователя (заполняется при source=roles). */
    roles: z.array(z.string()),
    /**
     * Группы Keycloak, в которых состоит человек. Для `source=roles` — те, через
     * которые он получил роль (по ним видно подразделение); для `source=group` —
     * запрошенная; для `source=search` — все его группы.
     */
    groups: z.array(z.string()),
    status: KeycloakCandidateStatusSchema,
    /** id уже существующей записи Lens (для link / exists / conflict). */
    lensUserId: z.string().uuid().nullable(),
});
/** Общая часть запроса: чем именно выбираем пользователей в Keycloak. */
const KeycloakSelectionSchema = z.object({
    source: KeycloakUserSourceSchema.default('roles'),
    /** Строка поиска (source=search) или имя группы (source=group). */
    query: z.string().trim().max(200).optional(),
    limit: z.number().int().min(1).max(1000).default(200),
});
/** POST /api/admin/sso/keycloak/preview — кого нашли и что с ними будет. */
export const KeycloakPreviewRequestSchema = KeycloakSelectionSchema;
export const KeycloakPreviewResponseSchema = z.object({
    candidates: z.array(KeycloakCandidateSchema),
    /** Сколько нашли всего (до обрезки по `limit`). */
    total: z.number(),
    /** Список обрезан по `limit` — в Keycloak есть ещё. */
    truncated: z.boolean(),
    /** Имена ролей, по которым собирали (для подписи в UI). */
    accessRole: z.string(),
    adminRole: z.string(),
    /**
     * Что не удалось прочитать в Keycloak, хотя результат всё же отдали
     * (напр. нет права на список клиентов → client-роли не проверялись).
     * Молча урезанная выборка выглядит как «в Keycloak никого нет».
     */
    warnings: z.array(z.string()),
});
/**
 * POST /api/admin/sso/keycloak/import — завести выбранных.
 *
 * Выборка повторяется на сервере по тем же `source`/`query`, а `keycloakIds`
 * только фильтруют результат: данные пользователей берём из Keycloak, а не
 * из тела запроса.
 */
export const KeycloakImportRequestSchema = KeycloakSelectionSchema.extend({
    keycloakIds: z.array(z.string().min(1)).min(1).max(1000),
});
export const KeycloakImportResultSchema = z.object({
    keycloakId: z.string(),
    login: z.string(),
    outcome: z.enum(['created', 'linked', 'skipped']),
    /** Причина пропуска (для outcome=skipped). */
    reason: z.string().nullable(),
    /** id записи Lens (для created / linked). */
    lensUserId: z.string().uuid().nullable(),
});
export const KeycloakImportResponseSchema = z.object({
    created: z.number(),
    linked: z.number(),
    skipped: z.number(),
    results: z.array(KeycloakImportResultSchema),
});
// ─── Глобальный журнал действий ─────────────────────────────────────────
//
// `GET /api/admin/activity` (см. `docs/api-design.md §4.x`,
// `docs/REQUIREMENTS.md FR-7.3`). Платформенный админ видит единую ленту
// действий всех сотрудников, собранную из четырёх источников:
//   • `record` — изменения записей из `audit_log` (по всем пространствам);
//   • `auth`   — события входа/безопасности/управления пользователями из
//                `auth_log`;
//   • `config` — изменения конфигурации (пространства, типы, поля,
//                справочники, представления) из `config_audit_log`;
//   • `access` — открытия пространств и карточек из `access_log`
//                (`space_opened` / `item_opened`).
export const ActivitySourceSchema = z.enum(['record', 'auth', 'config', 'access']);
/** Тип конфигурационной сущности (для source=config). */
export const ConfigEntityTypeSchema = z.enum([
    'space',
    'space_member',
    /** Привязка группы Keycloak к пространству. */
    'space_group',
    /** Группа пользователей внутри пространства (заготовка для полей типа `user`). */
    'space_user_group',
    'item_type',
    'field',
    'dictionary',
    'dictionary_column',
    'dictionary_editor',
    'dictionary_viewer',
    'view',
]);
export const ActivityLogEntrySchema = z.object({
    /** `${source}:${rawId}` — стабильный rowKey, источники не пересекаются. */
    id: z.string(),
    source: ActivitySourceSchema,
    /** Сырой код действия: `action` для record, `event` для auth. */
    action: z.string(),
    /** ISO-таймстамп события. */
    at: z.string(),
    /** Кто совершил действие. null = системное/неизвестно. */
    actor: z
        .object({ id: z.string().uuid(), login: z.string(), displayName: z.string() })
        .nullable(),
    /** Пространство записи (только для source=record). */
    space: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    /** Запись, которую затронули (только для source=record). */
    item: z
        .object({
        id: z.string().uuid(),
        displayKey: z.string().nullable(),
        title: z.string().nullable(),
    })
        .nullable(),
    /** Поле записи при field_change/status_change. */
    field: z.object({ slug: z.string(), name: z.string() }).nullable(),
    /** Конфигурационная сущность (только для source=config): тип, id, имя-снимок. */
    entity: z
        .object({
        type: ConfigEntityTypeSchema,
        id: z.string().nullable(),
        name: z.string().nullable(),
    })
        .nullable(),
    oldValue: z.unknown().nullable(),
    newValue: z.unknown().nullable(),
    /** Для auth-событий: логин затронутого аккаунта. */
    targetLogin: z.string().nullable(),
    /** Для auth-событий: IP-адрес. */
    ip: z.string().nullable(),
    details: z.record(z.unknown()),
});
export const ActivityLogResponseSchema = z.object({
    entries: z.array(ActivityLogEntrySchema),
    /** Курсор (ISO createdAt) для следующей страницы. null = конец. */
    nextCursor: z.string().nullable(),
});
export const ActivityLogQuerySchema = z.object({
    source: ActivitySourceSchema.optional(),
    /** Фильтр по актору (кто совершил). */
    userId: z.string().uuid().optional(),
    /** Фильтр по пространству (исключает auth-источник). */
    spaceId: z.string().uuid().optional(),
    /** Точный код действия/события. */
    action: z.string().max(64).optional(),
    /** Нижняя граница периода (ISO, включительно). */
    from: z.string().optional(),
    /** Верхняя граница периода (ISO, включительно). */
    to: z.string().optional(),
    /** Пагинация: вернуть события строго раньше этого ISO createdAt. */
    before: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
